import { SuppliesSubscriptionOrderInterval } from "@redotech/cockroachdb-codegen/tables/supplies-subscription-order-interval";
import { SuppliesSubscriptionStatus } from "@redotech/cockroachdb-codegen/tables/supplies-subscription-status";
import {
  suppliesSubscriptionIdFormat,
  suppliesSubscriptionOrderIdFormat,
} from "@redotech/redo-marketplace-id";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

const SubscriptionDestinationSchema = z.object({
  // Exactly one of these is set, mirroring the checkout destination.
  warehouseName: z.string().nullable(),
  address: z
    .object({
      name: z.string().nullable(),
      address1: z.string(),
      address2: z.string().nullable(),
      city: z.string(),
      region: z.string().nullable(),
      postalCode: z.string(),
      countryCode: z.string(),
    })
    .nullable(),
});

const SuppliesSubscriptionDetailSchema = z.object({
  id: suppliesSubscriptionIdFormat.schema,
  productTitle: z.string(),
  productSku: z.string().nullable(),
  productImageUrl: z.string().nullable(),
  quantity: z.number(),
  orderInterval: z.nativeEnum(SuppliesSubscriptionOrderInterval),
  orderIntervalFrequency: z.number(),
  orderDayOfWeek: z.number().nullable(),
  orderDayOfMonth: z.number().nullable(),
  nextReorderDate: zExt.plainDate(),
  status: z.nativeEnum(SuppliesSubscriptionStatus),
  // Timestamp of when the subscription was created.
  createdAt: z.string(),
  ordersPlacedCount: z.number(),
  // Projected next order dates; empty unless active.
  upcomingOrderDates: z.array(zExt.plainDate()),
  destination: SubscriptionDestinationSchema,
});

export type SuppliesSubscriptionDetail = z.infer<
  typeof SuppliesSubscriptionDetailSchema
>;

const SuppliesSubscriptionOrderRowSchema = z.object({
  id: suppliesSubscriptionOrderIdFormat.schema,
  // Timestamp of when the order was actually placed.
  date: z.string(),
  // Calendar cycle this placement covers (may differ from date if placed early).
  periodStartDate: zExt.plainDate(),
  // Mongo Order ObjectId (hex) of the placed supplies order.
  orderId: z.string(),
  orderName: z.string().nullable(),
  quantityOrdered: z.number(),
});

export type SuppliesSubscriptionOrderRow = z.infer<
  typeof SuppliesSubscriptionOrderRowSchema
>;

export const getSuppliesSubscriptionDetail = {
  name: "getSuppliesSubscriptionDetail" as const,
  input: z.object({ subscriptionId: suppliesSubscriptionIdFormat.schema }),
  output: z.object({ subscription: SuppliesSubscriptionDetailSchema }),
};

export const getSuppliesSubscriptionOrders = {
  name: "getSuppliesSubscriptionOrders" as const,
  input: z.object({
    subscriptionId: suppliesSubscriptionIdFormat.schema,
    pageNumber: z.number(),
    pageSize: z.number(),
  }),
  output: z.object({ orders: z.array(SuppliesSubscriptionOrderRowSchema) }),
};

export const getSuppliesSubscriptionOrdersCount = {
  name: "getSuppliesSubscriptionOrdersCount" as const,
  input: z.object({ subscriptionId: suppliesSubscriptionIdFormat.schema }),
  output: z.object({ total: z.number() }),
};
